// Review and submit.
//
// The client CAN submit with questions outstanding, deliberately. A
// partly-filled questionnaire is far more useful to the preparer than an
// abandoned one, and a hard gate is how you get a client who gives up at
// question eighty-five.
//
// So the outstanding list is information, not an obstacle: each item is
// tappable and jumps straight to its section.

import type { Outstanding, Progress } from '@myshoebox/shared';
import { useState } from 'react';
import { ApiError } from '../api/client.js';

export interface ReviewProps {
  year: number;
  progress: Progress;
  outstanding: Outstanding[];
  attachmentCount: number;
  status: string;
  onBack: () => void;
  onJumpTo: (sectionCode: string) => void;
  onSubmit: () => Promise<void>;
}

export function Review({
  year,
  progress,
  outstanding,
  attachmentCount,
  status,
  onBack,
  onJumpTo,
  onSubmit,
}: ReviewProps): JSX.Element {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const alreadySubmitted =
    status === 'submitted' || status === 'in_review' || status === 'complete';

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    setError(undefined);
    try {
      await onSubmit();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Could not send it. Please try again.',
      );
      setSubmitting(false);
    }
  };

  return (
    <div className="review">
      <header className="appbar">
        <button type="button" className="btn btn--ghost" onClick={onBack}>
          ‹ Back
        </button>
        <div className="brand brand--small">MyShoebox</div>
        <span />
      </header>

      <main className="container">
        <h1>{outstanding.length === 0 ? 'All done' : 'Nearly there'}</h1>

        <dl className="summary">
          <div className="summary__row">
            <dt>Financial year</dt>
            <dd>
              1 July {year - 1} – 30 June {year}
            </dd>
          </div>
          <div className="summary__row">
            <dt>Questions answered</dt>
            <dd>
              {progress.answered} of {progress.total}
            </dd>
          </div>
          <div className="summary__row">
            <dt>Documents attached</dt>
            <dd>{attachmentCount}</dd>
          </div>
        </dl>

        {outstanding.length > 0 ? (
          <section className="outstanding">
            <h2>{outstanding.length} still to answer</h2>
            <p className="muted small">
              You can send it now and add these later — but the more we have, the faster we can
              lodge. Tap any of them to go there.
            </p>
            <ul className="outstanding__list">
              {outstanding.map((item) => (
                <li key={item.code}>
                  <button type="button" onClick={() => onJumpTo(item.sectionCode)}>
                    <span className="outstanding__label">{item.label}</span>
                    <span className="outstanding__where">{item.sectionTitle}</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : (
          <p className="notice notice--good">
            Everything we asked has an answer. Thank you — that makes this much quicker on our end.
          </p>
        )}

        {error && (
          <p className="notice notice--error" role="alert">
            {error}
          </p>
        )}

        <button
          type="button"
          className="btn btn--primary btn--block btn--large"
          onClick={() => void submit()}
          disabled={submitting}
        >
          {submitting
            ? 'Sending…'
            : alreadySubmitted
              ? 'Send the updates'
              : 'Send to my accountant'}
        </button>

        {/* The reassurance that matters most, and the thing the paper process
            could not offer: sending is not a deadline. */}
        <p className="muted small review__note">
          Sending just tells us you are ready. You can still come back and add documents or change
          answers whenever you like — nothing is ever locked.
        </p>
      </main>
    </div>
  );
}
